import { dispatchFabricEvent } from '../../../hooks/useFabricEvent.js';
import { Mark, markPasteRule, markInputRule, mergeAttributes } from '@tiptap/core';
import { v4 } from 'uuid';
import { AnnotationPlugin, findAnnotationPosition } from './AnnotationPlugin.js';
import { noop } from '@fabric-app/utils/guards/noop';
import { getFbcDocumentResourceId } from '../FbcDocument.js';
import { colorToRGBStyle } from '../../../annotation/classes/DOMModel.js';
import { ANNOTATION_DEFAULT_COLOR } from '../../../annotation/config.js';
import { captureException } from '@fabric-app/compat/capabilities/sentry';

const inputRegex = /(?:^|\s)(==(?!\s+==)((?:[^=]+))==(?!\s+==))$/;
const pasteRegex = /(?:^|\s)(==(?!\s+==)((?:[^=]+))==(?!\s+==))/g;
const AnnotationMark = Mark.create({
  name: "annotation",
  addOptions() {
    return {
      handleDeleteComment: noop
    };
  },
  addStorage() {
    return {
      handleDeleteComment: this.options.handleDeleteComment ?? noop,
      handleCreateEmptyComment: this.options.handleCreateEmptyComment ?? noop
    };
  },
  addAttributes() {
    return {
      annotationId: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-annotation-id"),
        renderHTML: (attributes) => {
          if (!attributes.annotationId) {
            return {};
          }
          return {
            "data-annotation-id": attributes.annotationId
          };
        }
      },
      commentId: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-comment-id"),
        renderHTML: (attributes) => {
          if (!attributes.commentId) {
            return {};
          }
          return {
            "data-comment-id": attributes.commentId
          };
        }
      },
      color: {
        default: "rgb(253,214,96)",
        parseHTML: (element) => element.getAttribute("data-color"),
        renderHTML: (attributes) => {
          if (!attributes.color) {
            return {};
          }
          return {
            "data-color": attributes.color
          };
        }
      }
    };
  },
  parseHTML() {
    return [
      {
        priority: 50,
        tag: "mark"
      }
    ];
  },
  renderHTML({ HTMLAttributes }) {
    const mark = document.createElement("mark");
    const attributes = mergeAttributes(HTMLAttributes, {
      "data-annotation": "true"
    });
    Object.entries(attributes).forEach(([key, value]) => {
      mark.setAttribute(key, value);
    });
    const styles = colorToRGBStyle(HTMLAttributes["data-color"] ?? ANNOTATION_DEFAULT_COLOR);
    Object.entries(styles).forEach(([key, value]) => {
      mark.style.setProperty(key, value);
    });
    return mark;
  },
  addCommands() {
    return {
      createAnnotation: (highlight = false) => {
        return ({ chain, editor, state }) => {
          const resourceId = getFbcDocumentResourceId(editor.state);
          if (!resourceId) return false;
          const annotationId = v4();
          const result = chain().setMark("annotation", {
            annotationId,
            color: ANNOTATION_DEFAULT_COLOR
          }).run();
          if (!result) return false;
          const content = editor.state.selection.content().content;
          const text = content.textBetween(0, content.size, " ");
          if (highlight) {
            this.storage.handleCreateEmptyComment?.({
              id: annotationId,
              notepad: {
                color: ANNOTATION_DEFAULT_COLOR,
                elementId: annotationId,
                text
              }
            });
            return chain().setTextSelection(state.selection.to).focus().run();
          } else {
            dispatchFabricEvent("fabric-annotation-action-create", {
              type: "create-annotation",
              annotation: {
                id: annotationId,
                notepad: {
                  color: ANNOTATION_DEFAULT_COLOR,
                  elementId: annotationId,
                  text
                }
              },
              resourceId
            });
          }
          return true;
        };
      },
      deleteAnnotation: (annotationId) => {
        return ({ tr, editor }) => {
          const result = findAnnotationPosition(editor.state.doc, {
            annotationId
          });
          if (!result) return false;
          try {
            if (result.commentId) this.storage.handleDeleteComment(result.commentId);
          } catch (error) {
            captureException(error);
          }
          tr.removeMark(result.start, result.end, this.type);
          return true;
        };
      },
      setHandleDeleteComment: (handleDeleteComment) => {
        return () => {
          this.storage.handleDeleteComment = handleDeleteComment;
          return true;
        };
      },
      setHandleCreateEmptyComment: (handleCreateEmptyComment) => {
        return () => {
          this.storage.handleCreateEmptyComment = handleCreateEmptyComment;
          return true;
        };
      }
    };
  },
  addProseMirrorPlugins() {
    return [
      AnnotationPlugin(this.editor)
    ];
  },
  addKeyboardShortcuts() {
    return {
      "Mod-Shift-h": () => this.editor.commands.createAnnotation(true)
    };
  },
  addInputRules() {
    return [
      markInputRule({
        find: inputRegex,
        type: this.type,
        getAttributes: () => ({
          annotationId: v4(),
          color: ANNOTATION_DEFAULT_COLOR
        })
      })
    ];
  },
  addPasteRules() {
    return [
      markPasteRule({
        find: pasteRegex,
        type: this.type,
        getAttributes: () => ({
          annotationId: v4(),
          color: ANNOTATION_DEFAULT_COLOR
        })
      })
    ];
  }
});

export { AnnotationMark as default, inputRegex, pasteRegex };
//# sourceMappingURL=AnnotationMark.js.map
